import { useState } from "react";
import { useSalon } from "../context/SalonContext";
import { THEMES } from "../data/seed";

/**
 * LivePreview - renders a stylized browser frame showing either the public
 * salon website or the totem of the configured tenant.
 */
const LivePreview = () => {
    const { brand, theme } = useSalon();
    const [mode, setMode] = useState("site"); // 'site' | 'totem'

    const url = brand.customDomainStatus === "active" && brand.customDomain
        ? `https://${brand.customDomain.replace(/^https?:\/\//, "").replace(/\/$/, "")}`
        : `https://${brand.shopId}.kinng.app`;

    const tenantClass =
        theme.id === "vintage" ? "theme-vintage leather-texture"
        : theme.id === "neon" ? "theme-neon neon-texture"
        : "theme-organic organic-texture";

    return (
        <div data-testid="live-preview" className="rounded-2xl border border-white/10 bg-slate-950 overflow-hidden shadow-2xl">
            {/* browser chrome */}
            <div className="flex items-center gap-2 px-4 py-3 border-b border-white/5 bg-slate-900/80">
                <div className="flex items-center gap-1.5">
                    <span className="h-3 w-3 rounded-full bg-red-400/80"></span>
                    <span className="h-3 w-3 rounded-full bg-yellow-400/80"></span>
                    <span className="h-3 w-3 rounded-full bg-emerald-400/80"></span>
                </div>
                <div
                    data-testid="preview-url"
                    className="ml-3 flex-1 px-3 py-1.5 rounded-md bg-slate-950/60 border border-white/5 text-xs font-mono text-slate-300 truncate"
                >
                    {brand.customDomainStatus === "active" && (
                        <i className="fa-solid fa-lock text-emerald-400 mr-2"></i>
                    )}
                    {url}
                </div>
                <div className="flex items-center gap-1 ml-2">
                    <button
                        data-testid="preview-mode-site"
                        onClick={() => setMode("site")}
                        className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all min-h-[36px] ${
                            mode === "site" ? "bg-white text-slate-900" : "text-slate-400 hover:text-white"
                        }`}
                    >
                        <i className="fa-solid fa-globe mr-1.5"></i>Site
                    </button>
                    <button
                        data-testid="preview-mode-totem"
                        onClick={() => setMode("totem")}
                        className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all min-h-[36px] ${
                            mode === "totem" ? "bg-white text-slate-900" : "text-slate-400 hover:text-white"
                        }`}
                    >
                        <i className="fa-solid fa-desktop mr-1.5"></i>Totem
                    </button>
                </div>
            </div>

            {/* iframe-like viewport */}
            <div className={`${tenantClass} min-h-[560px] relative`}
                style={{ background: "var(--salon-bg)", color: "var(--salon-text)" }}
            >
                {mode === "site" ? <PublicSite /> : <TotemPreview />}
            </div>
        </div>
    );
};

const PublicSite = () => {
    const { brand, theme } = useSalon();
    return (
        <div className="relative">
            <div
                className="h-72 relative overflow-hidden"
                style={{
                    backgroundImage: `linear-gradient(180deg, transparent 0%, var(--salon-bg) 100%), url(${brand.heroImage})`,
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                }}
            >
                <div className="absolute inset-0 bg-gradient-to-t from-[var(--salon-bg)] via-[var(--salon-bg)]/30 to-transparent"></div>
                <div className="relative px-8 pt-6 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <div
                            className="h-9 w-9 rounded-lg flex items-center justify-center"
                            style={{ background: brand.primaryColor }}
                        >
                            <i className={`fa-solid fa-scissors text-white`}></i>
                        </div>
                        <span className="salon-heading text-lg" style={{ color: "var(--salon-text)" }}>
                            {brand.shopName}
                        </span>
                    </div>
                    <div className="flex items-center gap-5 text-xs salon-body" style={{ color: "var(--salon-muted)" }}>
                        <span>Serviços</span>
                        <span>Time</span>
                        <span>Contato</span>
                        <button
                            className="px-4 py-2 rounded-full text-white text-xs font-semibold uppercase tracking-wider"
                            style={{ background: brand.primaryColor }}
                        >
                            Agendar
                        </button>
                    </div>
                </div>
            </div>

            <div className="px-8 pb-8 -mt-24 relative z-10">
                <h1
                    className="salon-heading text-3xl md:text-5xl max-w-2xl leading-[1.05]"
                    style={{ color: "var(--salon-text)" }}
                    data-testid="preview-banner-title"
                >
                    {brand.bannerTitle}
                </h1>
                <p className="mt-3 max-w-xl text-sm md:text-base salon-body" style={{ color: "var(--salon-muted)" }}>
                    {brand.bannerSubtitle}
                </p>
                <div className="mt-6 flex gap-3">
                    <button
                        className="px-5 py-3 rounded-full text-white text-sm font-semibold uppercase tracking-wider min-h-[44px] transition-all hover:scale-[1.03]"
                        style={{ background: brand.primaryColor }}
                    >
                        Agendar agora <i className="fa-solid fa-arrow-right ml-2"></i>
                    </button>
                    <button
                        className="px-5 py-3 rounded-full border text-sm font-semibold uppercase tracking-wider min-h-[44px]"
                        style={{ borderColor: "var(--salon-border)", color: "var(--salon-text)" }}
                    >
                        Ver Serviços
                    </button>
                </div>

                <div className="mt-10 grid grid-cols-3 gap-3">
                    {theme.slogans.slice(0, 3).map((s) => (
                        <div
                            key={s}
                            className="p-4 rounded-xl text-xs salon-body"
                            style={{
                                background: "var(--salon-surface)",
                                border: "1px solid var(--salon-border)",
                                color: "var(--salon-muted)",
                            }}
                        >
                            <i className="fa-solid fa-quote-left mb-2" style={{ color: brand.primaryColor }}></i>
                            <p style={{ color: "var(--salon-text)" }}>{s}</p>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

const TotemPreview = () => {
    const { brand, theme } = useSalon();
    const img = brand.totemImages[0] || brand.heroImage;
    const now = new Date();
    const hh = now.getHours().toString().padStart(2, "0");
    const mm = now.getMinutes().toString().padStart(2, "0");
    return (
        <div className="relative h-[560px] overflow-hidden">
            <div
                className="absolute inset-0"
                style={{
                    backgroundImage: `url(${img})`,
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                }}
            ></div>
            <div className="absolute inset-0 bg-gradient-to-t from-black/95 via-black/40 to-black/60"></div>
            <div className="relative h-full flex flex-col items-center justify-center text-center px-8">
                <div
                    className="h-16 w-16 rounded-2xl flex items-center justify-center mb-4"
                    style={{ background: brand.primaryColor }}
                >
                    <i className="fa-solid fa-scissors text-white text-2xl"></i>
                </div>
                <div className="salon-heading text-4xl md:text-5xl text-white">
                    {brand.totemHeadline || brand.shopName}
                </div>
                <div className="mt-2 text-sm md:text-base text-white/70 max-w-md">
                    {brand.totemSubhead}
                </div>
                <div
                    className="mt-8 text-6xl md:text-7xl font-mono font-bold tabular-nums"
                    style={{ color: brand.primaryColor }}
                >
                    {hh}<span className="opacity-50">:</span>{mm}
                </div>
                <div className="mt-2 text-[11px] uppercase tracking-[0.4em] text-white/40">
                    {theme.slogans[0]}
                </div>
            </div>
        </div>
    );
};

const BrandStudio = () => {
    const { brand, updateBrand, theme, setTheme } = useSalon();
    const [domainStatus, setDomainStatus] = useState(brand.customDomainStatus);
    const [customDomainInput, setCustomDomainInput] = useState(brand.customDomain);

    const validateDomain = () => {
        if (!customDomainInput.trim()) return;
        setDomainStatus("checking");
        updateBrand({ customDomain: customDomainInput.trim(), customDomainStatus: "checking" });
        setTimeout(() => {
            setDomainStatus("active");
            updateBrand({ customDomainStatus: "active" });
        }, 3200);
    };

    const resetDomain = () => {
        setDomainStatus("idle");
        setCustomDomainInput("");
        updateBrand({ customDomain: "", customDomainStatus: "idle" });
    };

    return (
        <div data-testid="brand-studio-panel" className="grid xl:grid-cols-[420px_1fr] gap-6">
            {/* === Left: form === */}
            <div className="space-y-5 overflow-y-auto pr-2 max-h-[calc(100vh-130px)]">
                <header>
                    <div className="text-[10px] uppercase tracking-[0.3em] text-slate-500">
                        Módulo B · CRM de Marketing
                    </div>
                    <h2 className="salon-heading text-2xl mt-1 text-white">Estúdio de Marca</h2>
                    <p className="text-sm text-slate-400 mt-1">
                        Customize a identidade visual do seu salão. Tudo atualiza em tempo real.
                    </p>
                </header>

                {/* Theme selector */}
                <section className="rounded-2xl border border-white/5 bg-slate-900/40 p-4">
                    <div className="text-[11px] uppercase tracking-[0.25em] text-slate-400 mb-3">
                        Tema Base
                    </div>
                    <div className="grid grid-cols-1 gap-2">
                        {Object.values(THEMES).map((t) => {
                            const active = brand.themeId === t.id;
                            return (
                                <button
                                    key={t.id}
                                    data-testid={`theme-${t.id}-btn`}
                                    onClick={() => setTheme(t.id)}
                                    className={`min-h-[64px] flex items-center gap-3 px-4 py-3 rounded-xl border transition-all ${
                                        active
                                            ? "border-white/20 bg-slate-950/60"
                                            : "border-white/5 hover:border-white/10 bg-slate-950/30"
                                    }`}
                                >
                                    <div
                                        className="h-10 w-10 rounded-lg shrink-0"
                                        style={{
                                            background: `linear-gradient(135deg, ${t.primary}, ${t.primary}66)`,
                                        }}
                                    ></div>
                                    <div className="text-left flex-1">
                                        <div className="text-sm font-semibold text-white" style={{ fontFamily: t.headingFont }}>
                                            {t.name}
                                        </div>
                                        <div className="text-[11px] text-slate-500 truncate">{t.tagline}</div>
                                    </div>
                                    {active && <i className="fa-solid fa-check text-emerald-400"></i>}
                                </button>
                            );
                        })}
                    </div>
                </section>

                {/* Primary color picker */}
                <section className="rounded-2xl border border-white/5 bg-slate-900/40 p-4">
                    <div className="text-[11px] uppercase tracking-[0.25em] text-slate-400 mb-3">
                        Cor Principal
                    </div>
                    <div className="flex items-center gap-3">
                        <input
                            data-testid="color-picker"
                            type="color"
                            value={brand.primaryColor}
                            onChange={(e) => updateBrand({ primaryColor: e.target.value })}
                            className="h-12 w-16 rounded-lg cursor-pointer bg-transparent border border-white/10"
                        />
                        <div className="flex-1">
                            <input
                                value={brand.primaryColor}
                                onChange={(e) => updateBrand({ primaryColor: e.target.value })}
                                className="w-full bg-slate-950/60 border border-white/10 rounded-lg px-3 py-2 text-white font-mono text-sm outline-none focus:border-white/30"
                            />
                        </div>
                    </div>
                </section>

                {/* Logo icon */}
                <section className="rounded-2xl border border-white/5 bg-slate-900/40 p-4">
                    <div className="text-[11px] uppercase tracking-[0.25em] text-slate-400 mb-3">
                        Ícone do Logo
                    </div>
                    <div className="grid grid-cols-6 gap-2">
                        {["fa-scissors", "fa-spray-can-sparkles", "fa-user-tie", "fa-wand-magic-sparkles", "fa-crown", "fa-leaf"].map((ic) => {
                            const active = brand.logoIcon === ic.replace("fa-", "");
                            return (
                                <button
                                    key={ic}
                                    data-testid={`logo-icon-${ic}`}
                                    onClick={() => updateBrand({ logoIcon: ic.replace("fa-", "") })}
                                    className={`aspect-square min-h-[44px] rounded-lg flex items-center justify-center transition-all ${
                                        active
                                            ? "bg-white/10 border-white/30 text-white"
                                            : "bg-slate-950/60 border-white/5 text-slate-400 hover:text-white"
                                    } border`}
                                >
                                    <i className={`fa-solid ${ic}`}></i>
                                </button>
                            );
                        })}
                    </div>
                </section>

                {/* Text fields */}
                <section className="rounded-2xl border border-white/5 bg-slate-900/40 p-4 space-y-3">
                    <div className="text-[11px] uppercase tracking-[0.25em] text-slate-400 mb-1">
                        Textos & Conteúdo
                    </div>
                    <LabeledInput label="Nome do Salão" testId="input-shop-name"
                        value={brand.shopName} onChange={(v) => updateBrand({ shopName: v })} />
                    <LabeledInput label="Banner: Título" testId="input-banner-title"
                        value={brand.bannerTitle} onChange={(v) => updateBrand({ bannerTitle: v })} />
                    <LabeledInput label="Banner: Subtítulo" testId="input-banner-subtitle"
                        value={brand.bannerSubtitle} onChange={(v) => updateBrand({ bannerSubtitle: v })} multiline />
                    <LabeledInput label="Totem: Título" testId="input-totem-headline"
                        value={brand.totemHeadline} onChange={(v) => updateBrand({ totemHeadline: v })} />
                    <LabeledInput label="Totem: Subtítulo" testId="input-totem-subhead"
                        value={brand.totemSubhead} onChange={(v) => updateBrand({ totemSubhead: v })} />
                </section>

                {/* Image picker */}
                <section className="rounded-2xl border border-white/5 bg-slate-900/40 p-4">
                    <div className="text-[11px] uppercase tracking-[0.25em] text-slate-400 mb-3">
                        Imagem de Banner
                    </div>
                    <div className="grid grid-cols-3 gap-2">
                        {(theme.id === "vintage"
                            ? ["https://images.unsplash.com/photo-1778676688264-2a5db8886ab3", "https://images.unsplash.com/photo-1628274459813-74bc7e2c53b2", "https://images.unsplash.com/photo-1503951914875-452162b0f3f1"]
                            : theme.id === "neon"
                            ? ["https://images.unsplash.com/photo-1637777277337-f114350fb088", "https://images.unsplash.com/photo-1535637603896-07c179d71103", "https://images.unsplash.com/photo-1622286342621-4bd786c2447c"]
                            : ["https://images.unsplash.com/photo-1703792686756-c82bf734c89b", "https://images.unsplash.com/photo-1560066984-138dadb4c035", "https://images.unsplash.com/photo-1522337360788-8b13dee7a37e"]
                        ).map((base) => {
                            const url = base + "?w=600&q=80&auto=format&fit=crop";
                            const active = brand.heroImage.includes(base.split("/").pop());
                            return (
                                <button
                                    key={base}
                                    data-testid={`hero-img-${base.split("/").pop()}`}
                                    onClick={() => updateBrand({ heroImage: url, totemImages: [url, ...brand.totemImages.slice(1)] })}
                                    className={`aspect-video rounded-lg overflow-hidden border transition-all ${
                                        active ? "border-white ring-2 ring-white/40" : "border-white/10 hover:border-white/30"
                                    }`}
                                >
                                    <img src={url} alt="opção" className="h-full w-full object-cover" />
                                </button>
                            );
                        })}
                    </div>
                    <div className="mt-3">
                        <LabeledInput
                            label="ou cole uma URL própria"
                            testId="input-custom-hero"
                            value={brand.heroImage}
                            onChange={(v) => updateBrand({ heroImage: v })}
                        />
                    </div>
                </section>

                {/* Custom domain (Module C) */}
                <section
                    data-testid="domain-manager-section"
                    className="rounded-2xl border border-amber-500/20 bg-gradient-to-br from-slate-900/60 to-slate-950 p-4"
                >
                    <div className="flex items-center justify-between mb-3">
                        <div>
                            <div className="text-[11px] uppercase tracking-[0.25em] text-amber-400">
                                Módulo C
                            </div>
                            <div className="salon-heading text-lg text-white">Domínio Próprio</div>
                        </div>
                        <i className="fa-solid fa-globe text-amber-400 text-xl"></i>
                    </div>

                    <LabeledInput
                        label="Domínio personalizado"
                        testId="input-custom-domain"
                        value={customDomainInput}
                        onChange={setCustomDomainInput}
                        placeholder="www.salaodojao.com.br"
                    />

                    <div className="mt-3 rounded-xl border border-white/5 bg-slate-950/60 p-3 text-[11px] text-slate-400 leading-relaxed">
                        <p className="text-slate-300 mb-1.5 font-semibold uppercase tracking-wider text-[10px]">
                            <i className="fa-solid fa-circle-info mr-1.5 text-amber-400"></i>
                            Configure no seu provedor DNS
                        </p>
                        <ol className="list-decimal pl-4 space-y-1">
                            <li>Crie um registro <span className="font-mono text-amber-300">CNAME</span></li>
                            <li>Aponte para <span className="font-mono text-amber-300">cname.kinng.app</span></li>
                            <li>Aguarde propagação (até 24h)</li>
                        </ol>
                    </div>

                    {domainStatus === "idle" && (
                        <button
                            data-testid="validate-domain-btn"
                            onClick={validateDomain}
                            className="mt-4 w-full min-h-[48px] rounded-xl bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold uppercase tracking-[0.15em] text-xs transition-all"
                        >
                            Validar Apontamento e Ativar SSL
                        </button>
                    )}

                    {domainStatus === "checking" && (
                        <div data-testid="domain-checking" className="mt-4 space-y-2">
                            {["Resolvendo DNS…", "Verificando CNAME…", "Provisionando SSL…"].map((step, i) => (
                                <div
                                    key={step}
                                    className="relative overflow-hidden rounded-lg border border-amber-500/20 bg-slate-950/40 px-3 py-2.5 text-xs flex items-center gap-2"
                                >
                                    <i className="fa-solid fa-spinner fa-spin text-amber-400"></i>
                                    <span className="text-slate-300">{step}</span>
                                    <span className="dns-scan absolute inset-0 pointer-events-none"></span>
                                </div>
                            ))}
                        </div>
                    )}

                    {domainStatus === "active" && (
                        <div
                            data-testid="domain-active-status"
                            className="mt-4 rounded-xl border border-emerald-500/30 bg-emerald-500/5 p-4 flex items-center gap-3"
                        >
                            <div className="h-10 w-10 rounded-full bg-emerald-500/20 flex items-center justify-center">
                                <i className="fa-solid fa-shield-halved text-emerald-400"></i>
                            </div>
                            <div className="flex-1">
                                <div className="text-emerald-300 text-sm font-semibold">
                                    Domínio Ativo & SSL Validado
                                </div>
                                <div className="text-[11px] text-emerald-400/70 font-mono">
                                    https://{brand.customDomain}
                                </div>
                            </div>
                            <button
                                onClick={resetDomain}
                                className="text-[10px] uppercase tracking-wider text-slate-400 hover:text-white"
                            >
                                Remover
                            </button>
                        </div>
                    )}
                </section>
            </div>

            {/* === Right: live preview === */}
            <div className="space-y-4">
                <div className="flex items-center justify-between">
                    <div>
                        <div className="text-[10px] uppercase tracking-[0.3em] text-slate-500">
                            Pré-visualização em Tempo Real
                        </div>
                        <h3 className="salon-heading text-xl text-white mt-1">Live Preview</h3>
                    </div>
                    <div className="text-xs text-slate-400 flex items-center gap-2">
                        <span className="inline-flex h-2 w-2 rounded-full bg-emerald-400 animate-pulse"></span>
                        Sincronizado
                    </div>
                </div>
                <LivePreview />
            </div>
        </div>
    );
};

const LabeledInput = ({ label, value, onChange, testId, placeholder, multiline }) => (
    <label className="block">
        <span className="text-[10px] uppercase tracking-[0.2em] text-slate-400">{label}</span>
        {multiline ? (
            <textarea
                data-testid={testId}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                rows={2}
                placeholder={placeholder}
                className="mt-1 w-full bg-slate-950/60 border border-white/10 rounded-lg px-3 py-2 text-white text-sm outline-none focus:border-white/30 resize-none"
            />
        ) : (
            <input
                data-testid={testId}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                placeholder={placeholder}
                className="mt-1 w-full bg-slate-950/60 border border-white/10 rounded-lg px-3 py-2 text-white text-sm outline-none focus:border-white/30 min-h-[44px]"
            />
        )}
    </label>
);

export default BrandStudio;
